import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Plus, Check, Minus, ChevronDown, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtEUR = (n) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const resolveImg = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Phase 77 — Zubehör-Auswahl. Wird sowohl auf der Produktseite (productId)
 * als auch im Mietanfrage-Formular (ohne productId → alle aktiven) genutzt.
 * Liest/schreibt direkt in den CartContext (accessories state).
 * compact = dichte Zeilen-Darstellung, collapsible = eingeklappt mit Toggle-Header.
 */
const AccessoriesPicker = ({ productId = null, categoryIds = null, items: preloadedItems = null, title = 'Passendes Zubehör', compact = false, collapsible = false }) => {
  const [items, setItems] = useState(preloadedItems || []);
  const [loading, setLoading] = useState(preloadedItems === null);
  const [expanded, setExpanded] = useState(!collapsible);
  const { accessories, addAccessory, setAccessoryQuantity, removeAccessory } = useCart();

  const catKey = categoryIds ? categoryIds.join(',') : null;

  useEffect(() => {
    // Preloaded items provided by parent → no fetch needed.
    if (preloadedItems !== null) { setItems(preloadedItems); setLoading(false); return; }
    // Booking-form mode: only show accessories matching the selected products' categories.
    if (categoryIds !== null) {
      if (!categoryIds.length) { setItems([]); setLoading(false); return; }
      setLoading(true);
      const catSet = new Set(categoryIds);
      axios.get(`${API}/accessories`)
        .then((r) => {
          const all = r.data?.items || [];
          setItems(all.filter((a) => !a.category_ids?.length || a.category_ids.some((c) => catSet.has(c))));
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
      return;
    }
    // Product-page mode (filtered server-side by the product's categories).
    const url = productId ? `${API}/accessories?product_id=${encodeURIComponent(productId)}` : `${API}/accessories`;
    setLoading(true);
    axios.get(url)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [productId, catKey, preloadedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null;
  if (!items.length) return null;

  const inCart = (id) => accessories.find((a) => a.accessory.id === id);
  const selectedCount = items.reduce((n, acc) => n + (inCart(acc.id) ? 1 : 0), 0);

  const qtyControls = (acc, entry, max, small) => (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => entry.quantity <= 1 ? removeAccessory(acc.id) : setAccessoryQuantity(acc.id, entry.quantity - 1)}
        className={`${small ? 'h-7 w-7' : 'h-8 w-8'} rounded-full border-2 border-amber-300 text-amber-700 font-bold hover:bg-amber-50`}
        data-testid={`accessory-decrease-${acc.id}`}
      ><Minus className="h-3.5 w-3.5 mx-auto" /></button>
      <span className="w-6 text-center font-bold tabular-nums text-sm" data-testid={`accessory-qty-${acc.id}`}>{entry.quantity}</span>
      <button
        type="button"
        onClick={() => setAccessoryQuantity(acc.id, entry.quantity + 1)}
        disabled={entry.quantity >= max}
        className={`${small ? 'h-7 w-7' : 'h-8 w-8'} rounded-full border-2 border-amber-300 text-amber-700 font-bold hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed`}
        data-testid={`accessory-increase-${acc.id}`}
      ><Plus className="h-3.5 w-3.5 mx-auto" /></button>
    </div>
  );

  // Kompakte Zeilen-Darstellung (Anfrage-Formular & Upsell-Popup)
  const compactList = (
    <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
      {items.map((acc) => {
        const entry = inCart(acc.id);
        const img = resolveImg(acc.image_url);
        const max = Math.max(1, Math.min(10, Number(acc.max_quantity) || 10));
        return (
          <div
            key={acc.id}
            className={`flex items-center gap-2.5 rounded-xl border-2 px-2.5 py-2 transition ${entry ? 'border-amber-400 bg-amber-50/60' : 'border-neutral-200 bg-white hover:border-amber-300'}`}
            data-testid={`accessory-card-${acc.id}`}
            title={acc.description || acc.name}
          >
            <div className="h-10 w-10 shrink-0 rounded-lg overflow-hidden bg-amber-50 border border-amber-100 flex items-center justify-center">
              {img ? <img src={img} alt={acc.name} className="h-full w-full object-cover" loading="lazy" /> : <span className="text-lg">🎁</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-neutral-900 text-[13px] leading-tight truncate">{acc.name}</p>
              <p className="text-xs font-bold text-amber-700">{fmtEUR(acc.price)} <span className="font-normal text-neutral-500">/ Stück</span></p>
            </div>
            {!entry ? (
              <button
                type="button"
                onClick={() => addAccessory(acc, 1)}
                className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full bg-amber-500 hover:bg-amber-600 text-white transition"
                data-testid={`accessory-add-${acc.id}`}
                aria-label={`${acc.name} hinzufügen`}
              >
                <Plus className="h-4 w-4" />
              </button>
            ) : qtyControls(acc, entry, max, true)}
          </div>
        );
      })}
    </div>
  );

  // Karten-Darstellung (Produktseite)
  const cardList = (
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((acc) => {
        const entry = inCart(acc.id);
        const img = resolveImg(acc.image_url);
        const max = Math.max(1, Math.min(10, Number(acc.max_quantity) || 10));
        return (
          <div
            key={acc.id}
            className={`relative rounded-2xl border-2 p-3 transition ${entry ? 'border-amber-400 bg-amber-50/60' : 'border-neutral-200 bg-white hover:border-amber-300'}`}
            data-testid={`accessory-card-${acc.id}`}
          >
            <div className="flex gap-3">
              <div className="h-16 w-16 shrink-0 rounded-xl overflow-hidden bg-amber-50 border border-amber-100 flex items-center justify-center">
                {img ? <img src={img} alt={acc.name} className="h-full w-full object-cover" loading="lazy" /> : <span className="text-2xl">🎁</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-neutral-900 text-sm leading-tight">{acc.name}</p>
                {acc.description && (
                  <p className="text-xs text-neutral-500 line-clamp-2 mt-0.5">{acc.description}</p>
                )}
                <p className="text-sm font-bold text-amber-700 mt-1">{fmtEUR(acc.price)} <span className="text-[11px] font-normal text-neutral-500">/ Stück</span></p>
              </div>
            </div>
            {!entry ? (
              <button
                type="button"
                onClick={() => addAccessory(acc, 1)}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2 text-sm transition"
                data-testid={`accessory-add-${acc.id}`}
              >
                <Plus className="h-4 w-4" /> Hinzufügen
              </button>
            ) : (
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold">
                  <Check className="h-4 w-4" /> Im Warenkorb
                </div>
                {qtyControls(acc, entry, max, false)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (collapsible) {
    return (
      <div className={`rounded-2xl border-2 transition ${selectedCount > 0 ? 'border-amber-300 bg-amber-50/40' : 'border-neutral-200 bg-white'}`} data-testid="accessories-picker">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="w-full flex items-center gap-2.5 px-4 py-3 text-left"
          data-testid="accessories-toggle"
        >
          <Gift className="h-5 w-5 text-amber-600 shrink-0" />
          <span className="font-bold text-sky-800 text-sm md:text-base flex-1">
            {title || 'Passendes Zubehör'}
            <span className="text-xs font-normal text-neutral-500 ml-1.5">({items.length} verfügbar)</span>
          </span>
          {selectedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full" data-testid="accessories-selected-badge">
              <Check className="h-3 w-3" /> {selectedCount} ausgewählt
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-neutral-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        {expanded && <div className="px-3 pb-3">{compactList}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="accessories-picker">
      {title && (
        <div className="flex items-center gap-2">
          <h3 className={`font-bold text-sky-800 ${compact ? 'text-base' : 'text-lg md:text-xl'}`}>{title}</h3>
          <span className="text-xs text-neutral-500">({items.length})</span>
        </div>
      )}
      {compact ? compactList : cardList}
    </div>
  );
};

export default AccessoriesPicker;
